import React, { useEffect, useRef } from 'react';
import { useWhatsAppPreview } from '../../../hooks/useWhatsAppPreview';
import { MessageBubble } from './preview/MessageBubble';
import { ImageMessageBubble } from './preview/ImageMessageBubble';
import { AudioMessageBubble } from './preview/AudioMessageBubble';
import { VideoMessageBubble } from './preview/VideoMessageBubble';
import { DocumentMessageBubble } from './preview/DocumentMessageBubble';
import { Smartphone, Wifi, Battery, Trash2 } from 'lucide-react';
import { messagesApi } from '../../../services/messagesApi';
import { trainingGroupsApi } from '../../../services/trainingGroupsApi';
import { toast } from '../../../../../hooks/use-toast';
import { useReactFlow } from 'reactflow';

interface WhatsAppPreviewPanelProps {
  flowId: string | null;
  onFlowDataChange?: () => Promise<void>;
}

export const WhatsAppPreviewPanel: React.FC<WhatsAppPreviewPanelProps> = ({ flowId, onFlowDataChange }) => {
  const { groupedMessages, isLoading } = useWhatsAppPreview(flowId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { getNodes, setNodes } = useReactFlow();

  const handleDeleteMessage = async (nodeId: string) => {
    try {
      // 1. Borrar de training_messages
      await messagesApi.deleteMessage(nodeId);

      // 2. Borrar el nodo del canvas
      const currentNodes = getNodes();
      const updatedNodes = currentNodes.filter(node => node.id !== nodeId);
      setNodes(updatedNodes);

      // 3. Sincronizar con agent_training_flows
      if (onFlowDataChange) {
        await onFlowDataChange();
      }

      toast.success('Mensaje eliminado', {
        duration: 2000,
        position: 'bottom-left',
      });
    } catch (error) {
      console.error('Error eliminando mensaje:', error);
      toast.error('Error al eliminar mensaje', {
        duration: 2000,
        position: 'bottom-left',
      });
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm('¿Eliminar todo el grupo de mensajes?')) return;

    try {
      // 1. Obtener los mensajes del grupo para borrar los nodos
      const group = groupedMessages.find(g => g.groupId === groupId);
      const nodeIdsToDelete = group?.messages.map(m => m.nodeId) || [];

      // 2. Borrar de training_groups
      // CRÍTICO: pasar flowId — group_id es el número del grupo y NO es único entre flows.
      await trainingGroupsApi.deleteGroup(groupId, flowId);

      // 3. Borrar todos los mensajes del grupo
      await Promise.all(nodeIdsToDelete.map(nodeId => messagesApi.deleteMessage(nodeId)));

      // 4. Borrar los nodos del canvas
      const currentNodes = getNodes();
      const updatedNodes = currentNodes.filter(node => !nodeIdsToDelete.includes(node.id));
      setNodes(updatedNodes);

      // 5. Sincronizar con agent_training_flows
      if (onFlowDataChange) {
        await onFlowDataChange();
      }

      toast.success('Grupo eliminado', {
        duration: 2000,
        position: 'bottom-left',
      });
    } catch (error) {
      console.error('Error eliminando grupo:', error);
      toast.error('Error al eliminar grupo', {
        duration: 2000,
        position: 'bottom-left',
      });
    }
  };

  // Auto-scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [groupedMessages]);

  if (!flowId) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-100">
        <p className="text-sm text-gray-500">Selecciona o crea un flujo para ver la previsualización</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-100 flex flex-col">
      {/* Phone Container */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-[380px] h-full max-h-[720px] bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col border-8 border-gray-800">
          {/* Status Bar */}
          <div className="bg-[#075E54] px-6 py-2 flex items-center justify-between text-white">
            <span className="text-xs">9:41</span>
            <div className="flex items-center gap-1">
              <Wifi className="h-3 w-3" />
              <Battery className="h-3 w-3" />
            </div>
          </div>

          {/* WhatsApp Header */}
          <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Cliente</h3>
              <p className="text-xs opacity-80">en línea</p>
            </div>
          </div>

          {/* Messages Area */}
          <div
            className="flex-1 overflow-y-auto bg-[#E5DDD5] px-4 py-3"
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg opacity=\'0.05\'%3E%3Cpath d=\'M0 0h100v100H0z\' fill=\'%23000\'/%3E%3C/g%3E%3C/svg%3E")',
            }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-gray-500">Cargando mensajes...</p>
              </div>
            ) : groupedMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-gray-500 text-center">
                  No hay mensajes aún.<br />Agrega nodos al flujo para verlos aquí.
                </p>
              </div>
            ) : (
              <>
                {groupedMessages.map((group) => (
                  <div key={group.groupId} className="mb-6">
                    {/* Group Header */}
                    <div className="flex justify-center mb-3">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-800">
                          Grupo {group.groupNumber}
                        </span>
                        {group.instruction && (
                          <>
                            <span className="text-gray-400">•</span>
                            <p className="text-xs text-gray-700 font-medium">
                              {group.instruction}
                            </p>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteGroup(group.groupId)}
                          className="ml-2 p-1 hover:bg-red-100 rounded transition-colors"
                          title="Eliminar grupo"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </button>
                      </div>
                    </div>

                    {/* Messages in Group */}
                    {group.messages.map((message, messageIndex) => {
                      const key = `${group.groupId}-${message.id}-${messageIndex}`;

                      return (
                        <div key={key} className="relative group/message">
                          <button
                            onClick={() => handleDeleteMessage(message.nodeId)}
                            className="absolute -right-2 -top-2 z-10 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover/message:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                            title="Eliminar mensaje"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>

                          {message.type === 'text' && (
                            <MessageBubble content={message.content || ''} />
                          )}
                          {message.type === 'image' && (
                            <ImageMessageBubble
                              imageUrl={message.mediaUrl}
                              caption={message.caption}
                              processingStatus={message.aiProcessingStatus}
                            />
                          )}
                          {message.type === 'audio' && (
                            <AudioMessageBubble
                              audioUrl={message.mediaUrl}
                              fileName={message.fileName}
                              duration={message.duration}
                              aiTranscription={message.aiDescription}
                              processingStatus={message.aiProcessingStatus}
                            />
                          )}
                          {message.type === 'video' && (
                            <VideoMessageBubble
                              videoUrl={message.mediaUrl}
                              caption={message.caption}
                              fileName={message.fileName}
                            />
                          )}
                          {message.type === 'document' && (
                            <DocumentMessageBubble
                              documentUrl={message.mediaUrl}
                              caption={message.caption}
                              fileName={message.fileName}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area (static, non-functional) */}
          <div className="bg-[#F0F0F0] px-4 py-2 flex items-center gap-2">
            <div className="flex-1 bg-white rounded-full px-4 py-2">
              <p className="text-sm text-gray-400">Escribe un mensaje...</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#075E54] flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
